// Speak Up (speak; Learn's "Everyday words"): phrases you say. Each is a multiple-choice quiz on
// its meaning: the phrase is heard and shown ("お願いします", "onegaishimasu: what does it mean?"),
// with its meaning among three others from the same deck (topped up from the rest of the
// phrases). A new phrase is met first (its meaning shown), and a right answer flashes "Now say it
// out loud", which isn't graded. Owner, 23 Oct: the old self-rating (Nailed it / Close / Again)
// never showed what a phrase means. Word-listening cards are quick multiple choice too. A phrase
// needs its own `meaning` in the kit to be asked (cardsWithoutMeaning lists those that lack one).
import type { Card, Pack } from '../../../../shared/pack/schema'
import { choices, learnDecks, lookup, sceneFor } from '../cards'
import { isLearned } from '../srs'
import type { Item, CardSet } from '../types'
import { t } from '../../../strings/en'

// Its decks are the kit's: every deck marked `learn: ["speak"]` (B7).
const isNumeric = (s: string) => /^[\d:,]+$/.test(s)
type Phrase = Extract<Card, { kind: 'speak' | 'recall' }>
const isPhrase = (c: Card): c is Phrase => c.kind === 'speak' || c.kind === 'recall'

function wanted(c: Card): boolean {
  return (isPhrase(c) && !!c.meaning) || (c.kind === 'listen' && !isNumeric(c.answer))
}

/** The phrase in the trip's script ("お願いします") and in letters ("onegaishimasu"). */
function phraseOf(c: Phrase): { big: string; romaji: string } {
  if (c.kind === 'speak') return { big: c.target, romaji: (c.prompt.split(/Say it:\s*/i)[1] ?? '').trim() }
  return { big: c.say ?? c.answer, romaji: c.answer }
}

/** Phrase cards (speak, recall) in Speak Up's decks with no meaning to quiz on: they're left out. */
export function cardsWithoutMeaning(pack: Pack): string[] {
  return learnDecks(pack, 'speak').flatMap((d) => d.cards.filter((c) => isPhrase(c) && !c.meaning).map((c) => c.id))
}

export const speakUp: CardSet = {
  id: 'speak-up',
  kind: 'speak',
  title: 'Speak Up',
  blurb: t.arcade.blurbs['speak-up']!,
  theme: { glyph: '', jpGlyph: true, color: 'mint' },
  sessionSize: 4,
  newPerSession: 2,

  pool(pack: Pack) {
    return learnDecks(pack, 'speak').flatMap((d) => d.cards.filter(wanted).map((c) => c.id))
  },

  eligible: () => true,

  toItem(ctx, id, rand): Item | null {
    const hit = lookup(ctx.pack, id)
    if (!hit) return null
    const { card, deck } = hit
    const ctxPart = sceneFor(card, deck)
    if (isPhrase(card)) {
      if (!card.meaning) return null
      const { big, romaji } = phraseOf(card)
      // Wrong options: the same deck's phrases first (Hello, Thank you…), then the other decks'.
      const meaningsIn = (cards: readonly Card[]) => cards.flatMap((c) => (isPhrase(c) && c.meaning ? [c.meaning] : []))
      const far = this.pool(ctx.pack).flatMap((x) => {
        const c = lookup(ctx.pack, x)?.card
        return c && isPhrase(c) && c.meaning ? [c.meaning] : []
      })
      const c = choices(card.meaning, meaningsIn(deck.cards), far, rand)
      const say = card.say ?? big
      return {
        type: 'choice',
        cardId: id,
        ...ctxPart,
        big,
        listen: say,
        question: t.session.whatMeans(romaji),
        options: c.options,
        answer: c.answer,
        reveal: {
          big,
          line: t.session.means(romaji, card.meaning),
          say,
          ...(card.anecdote ? { note: card.anecdote } : {}),
          ...(romaji ? { example: t.session.sayIt(romaji) } : {}),
        },
        xp: card.xp,
      }
    }
    if (card.kind === 'listen') {
      const others = this.pool(ctx.pack)
        .map((x) => lookup(ctx.pack, x)?.card)
        .flatMap((c) => (c?.kind === 'listen' ? [c.answer] : []))
      const c = choices(card.answer, others, [], rand)
      return {
        type: 'choice',
        cardId: id,
        ...ctxPart,
        listen: card.say ?? '',
        question: 'What did they say?',
        options: c.options,
        answer: c.answer,
        reveal: { line: card.answer, ...(card.say ? { big: card.say, say: card.say } : {}), ...(card.anecdote ? { note: card.anecdote } : {}) },
        xp: card.xp,
      }
    }
    return null
  },

  progress(ctx) {
    const pool = this.pool(ctx.pack)
    return { label: 'Phrases', value: pool.filter((id) => isLearned(ctx.progress.cards[id])).length, max: pool.length }
  },
}
